import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import VisibilityIcon from "@mui/icons-material/Visibility";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import ContractorMasterContractApi from "../../../../../infrastructure/api/contractorMasterContractApi";
import BreadcrumbsComponent from "../../../../components/shared/breadcrumbs";
import JalaliDatePicker from "../../../../components/shared/jalali-datepicker";
import { toLocalDateInputValue } from "../../../../middleware/middleware";
import { ContractTimeline } from "../../shared/contracts/UniversalContractComponents";
import {
  CONTRACT_STATUS,
  readContractFlags,
  resolvePrimaryAction,
  resolveStepIndex,
  WORKFLOW_STEPS,
} from "./contractorMasterOperatorWorkflow";

const openGuaranteeBlob = (blob, fileName = "cheque-guarantee") => {
  if (!blob) return;
  const url = URL.createObjectURL(blob);
  window.open(url, "_blank", "noopener,noreferrer");
  // Keep object URL long enough for the tab to load; revoke later.
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
};

const openPdfBlob = (blob, fileName = "contractor-contract.pdf") => {
  if (!blob) return false;
  const pdfBlob =
    blob.type && String(blob.type).includes("pdf")
      ? blob
      : new Blob([blob], { type: "application/pdf" });
  const url = URL.createObjectURL(pdfBlob);
  const win = window.open(url, "_blank", "noopener,noreferrer");
  if (!win) {
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
  }
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
  return true;
};
/**
 * Guided operator console: only the next allowed step is primary.
 * Locks / prerequisites are enforced by flags + UI gating.
 */
const ContractorMasterContractOperatorPage = () => {
  const [searchParams] = useSearchParams();
  const [contractorInfoId, setContractorInfoId] = useState(
    () => searchParams.get("contractorInfoId") || ""
  );
  const [contractId, setContractId] = useState(
    () => searchParams.get("contractId") || ""
  );
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [otpCode, setOtpCode] = useState("");
  const [rejectReason, setRejectReason] = useState("");
  const [showLookup, setShowLookup] = useState(true);
  const [cheque, setCheque] = useState({
    sayadiId: "",
    chequeSerial: "",
    bankName: "",
    accountNumber: "",
    chequeDate: null,
  });
  const [receiptParties, setReceiptParties] = useState({
    bringerName: "",
    bringerMobile: "",
    bringerNationalCode: "",
    officeReceiptNotes: "",
  });
  const guaranteeInputRef = useRef(null);

  // Always show generate/lookup until a contract is loaded into the console.
  const lookupVisible = showLookup || !view;

  const flags = useMemo(() => (view ? readContractFlags(view) : null), [view]);
  const primary = useMemo(() => resolvePrimaryAction(flags), [flags]);
  const activeStep = useMemo(() => resolveStepIndex(flags), [flags]);

  const loadDetail = async (id) => {
    const cid = id || contractId;
    if (!cid) return;
    setLoading(true);
    const [detailRes, timelineRes] = await Promise.all([
      ContractorMasterContractApi.getById(cid),
      ContractorMasterContractApi.getTimeline(cid),
    ]);
    setLoading(false);
    if (!detailRes.isSuccess) {
      toast.error(detailRes.message || "بارگذاری قرارداد ناموفق بود.");
      return;
    }
    setView(detailRes.data);
    setContractId(String(detailRes.data?.contractId || detailRes.data?.ContractId || cid));
    const cidFromView =
      detailRes.data?.contractorInfoId || detailRes.data?.ContractorInfoId;
    if (cidFromView) setContractorInfoId(String(cidFromView));
    setTimeline(timelineRes.data?.events || timelineRes.data?.Events || []);
    setShowLookup(false);

    const d = detailRes.data || {};
    setCheque((prev) => ({
      ...prev,
      sayadiId: d.guaranteeSayadiId || d.GuaranteeSayadiId || prev.sayadiId || "",
      chequeSerial:
        d.guaranteeChequeSerial || d.GuaranteeChequeSerial || prev.chequeSerial || "",
      bankName: d.guaranteeBankName || d.GuaranteeBankName || prev.bankName || "",
      accountNumber:
        d.guaranteeAccountNumber ||
        d.GuaranteeAccountNumber ||
        prev.accountNumber ||
        "",
      chequeDate: (() => {
        const raw = d.guaranteeChequeDate || d.GuaranteeChequeDate;
        if (!raw) return prev.chequeDate || null;
        const date = new Date(raw);
        if (Number.isNaN(date.getTime())) return prev.chequeDate || null;
        return date;
      })(),
    }));
  };

  useEffect(() => {
    const fromQuery = searchParams.get("contractId");
    const contractorFromQuery = searchParams.get("contractorInfoId");
    if (contractorFromQuery) {
      setContractorInfoId(contractorFromQuery);
      setShowLookup(true);
    }
    if (fromQuery) {
      setContractId(fromQuery);
      loadDetail(fromQuery);
      return;
    }
    if (contractorFromQuery) {
      (async () => {
        setLoading(true);
        try {
          const res = await ContractorMasterContractApi.list({
            contractorInfoId: Number(contractorFromQuery),
            page: 1,
            pageSize: 5,
          });
          if (!res?.isSuccess) return;
          const items =
            res.data?.items ||
            res.data?.Items ||
            (Array.isArray(res.data) ? res.data : []);
          const latest = items[0];
          const latestId =
            latest?.contractId || latest?.ContractId || latest?.id || latest?.Id;
          if (latestId) {
            setContractId(String(latestId));
            await loadDetail(latestId);
          }
        } finally {
          setLoading(false);
        }
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const run = async (fn, successMsg) => {
    setLoading(true);
    try {
      const res = await fn();
      if (!res || !res.isSuccess) {
        toast.error(res?.message || "عملیات ناموفق بود.");
        return null;
      }
      toast.success(successMsg || "انجام شد.");
      return res.data;
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.Message ||
        err?.message ||
        "خطای ارتباط با سرور.";
      toast.error(msg);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = async () => {
    const data = await run(
      () => ContractorMasterContractApi.generate(contractorInfoId),
      "قرارداد تولید شد."
    );
    if (data?.contractId || data?.ContractId) {
      const id = data.contractId || data.ContractId;
      setContractId(String(id));
      await loadDetail(id);
    }
  };

  const handleUploadGuarantee = async (file) => {
    if (!file) return;
    const sayadiId = String(cheque.sayadiId || "").replace(/\D/g, "");
    const accountNumber = String(cheque.accountNumber || "").replace(/\D/g, "");
    const chequeSerial = String(cheque.chequeSerial || "").trim();
    const bankName = String(cheque.bankName || "").trim();
    if (sayadiId.length !== 16) {
      toast.error("شناسه صیادی باید ۱۶ رقم باشد.");
      return;
    }
    if (!chequeSerial || !bankName) {
      toast.error("سریال و نام بانک الزامی است.");
      return;
    }
    if (accountNumber.length < 6 || accountNumber.length > 20) {
      toast.error("شماره حساب باید بین ۶ تا ۲۰ رقم باشد.");
      return;
    }
    setLoading(true);
    const res = await ContractorMasterContractApi.uploadGuaranteeDocument(
      contractId,
      file,
      { sayadiId, chequeSerial, bankName, accountNumber }
    );
    setLoading(false);
    if (!res.isSuccess) {
      toast.error(res.message || "بارگذاری ناموفق بود.");
      return;
    }
    toast.success("مدرک ضمانت بارگذاری شد.");
    await loadDetail();
  };

  const handleViewGuaranteeDocument = async () => {
    setLoading(true);
    try {
      const blob = await ContractorMasterContractApi.downloadGuaranteeDocument(
        contractId
      );
      if (!blob) {
        toast.error("مدرک ضمانت یافت نشد.");
        return;
      }
      openGuaranteeBlob(blob);
    } catch (_err) {
      toast.error("مشاهده مدرک ضمانت ناموفق بود.");
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPdf = async () => {
    if (!contractId) return;
    setLoading(true);
    try {
      const preferSigned = Boolean(flags?.signedPdfUrl);
      const res = await ContractorMasterContractApi.downloadPdf(contractId, {
        preferSigned,
      });
      if (!res.isSuccess || !res.data) {
        toast.error(res.message || "دانلود PDF ناموفق بود.");
        return;
      }
      openPdfBlob(
        res.data,
        preferSigned
          ? `contractor-master-${contractId}-signed.pdf`
          : `contractor-master-${contractId}.pdf`
      );
    } catch (_err) {
      toast.error("دانلود PDF ناموفق بود.");
    } finally {
      setLoading(false);
    }
  };

  const handleRegenerateSignedPdf = async () => {
    if (!contractId) return;
    setLoading(true);
    try {
      const res = await ContractorMasterContractApi.downloadPdf(contractId, {
        preferSigned: true,
        forceRegenerate: true,
      });
      if (!res.isSuccess || !res.data) {
        toast.error(res.message || "بازتولید PDF امضاشده ناموفق بود.");
        return;
      }
      openPdfBlob(res.data, `contractor-master-${contractId}-signed.pdf`);
      toast.success("PDF امضاشده با لینک و تاریخ به‌روز بازتولید شد.");
    } catch (_err) {
      toast.error("بازتولید PDF امضاشده ناموفق بود.");
    } finally {
      setLoading(false);
    }
  };

  const handleRegenerateContent = async () => {
    if (!contractId) return;
    const data = await run(
      () => ContractorMasterContractApi.regenerateContent(contractId),
      "محتوای قرارداد با اطلاعات به‌روز پلتفرم بازتولید شد."
    );
    if (data) await loadDetail(contractId);
  };

  const handleCreateRevision = async () => {
    if (!contractId) return;
    const data = await run(
      () => ContractorMasterContractApi.createRevision(contractId),
      "نسخه جدید قرارداد ایجاد شد."
    );
    const newId = data?.contractId || data?.ContractId;
    if (newId) {
      setContractId(String(newId));
      await loadDetail(newId);
    } else if (data) {
      await loadDetail(contractId);
    }
  };

  const handleSetChequeDetails = async () => {
    const sayadiId = String(cheque.sayadiId || "").replace(/\D/g, "");
    const accountNumber = String(cheque.accountNumber || "").replace(/\D/g, "");
    const chequeSerial = String(cheque.chequeSerial || "").trim();
    const bankName = String(cheque.bankName || "").trim();
    if (sayadiId.length !== 16) {
      toast.error("شناسه صیادی باید ۱۶ رقم باشد.");
      return;
    }
    if (!chequeSerial || !bankName) {
      toast.error("سریال و نام بانک الزامی است.");
      return;
    }
    if (accountNumber.length < 6 || accountNumber.length > 20) {
      toast.error("شماره حساب باید بین ۶ تا ۲۰ رقم باشد.");
      return;
    }
    if (!cheque.chequeDate) {
      toast.error("تاریخ چک الزامی است.");
      return;
    }
    const chequeDateValue =
      typeof cheque.chequeDate === "string"
        ? toLocalDateInputValue(cheque.chequeDate) || cheque.chequeDate
        : toLocalDateInputValue(cheque.chequeDate);

    const data = await run(
      () =>
        ContractorMasterContractApi.setGuaranteeChequeDetails(contractId, {
          sayadiId,
          chequeSerial,
          bankName,
          accountNumber,
          chequeDate: chequeDateValue,
        }),
      "مشخصات چک در قرارداد ثبت و PDF به‌روز شد."
    );
    if (data) setView(data);
    else await loadDetail();
  };

  const handlePrimaryClick = async () => {
    if (!primary?.key) return;

    if (primary.key === "set_cheque_details") {
      await handleSetChequeDetails();
      return;
    }

    if (primary.key === "confirm_physical_receipt") {
      if (!receiptParties.bringerName?.trim()) {
        toast.error("نام آورنده چک الزامی است.");
        return;
      }
      const data = await run(
        () =>
          ContractorMasterContractApi.confirmPhysicalGuaranteeReceipt(contractId, {
            bringerName: receiptParties.bringerName.trim(),
            bringerMobile: receiptParties.bringerMobile || "",
            bringerNationalCode: receiptParties.bringerNationalCode || "",
            officeReceiptNotes: receiptParties.officeReceiptNotes || "",
          }),
        "وصول چک در دفتر ثبت شد. اکنون امضای نهایی باز است."
      );
      if (data) setView(data);
      else await loadDetail();
      return;
    }

    if (primary.key === "approve_for_signature") {
      const data = await run(
        () => ContractorMasterContractApi.approveForSignature(contractId),
        "قرارداد برای امضا تأیید شد."
      );
      if (data) setView(data);
      else await loadDetail();
      return;
    }

    if (primary.key === "send_contractor_signature") {
      const data = await run(
        () => ContractorMasterContractApi.sendForContractorSignature(contractId),
        "برای امضای پیمانکار ارسال شد."
      );
      if (data) setView(data);
      else await loadDetail();
      return;
    }

    if (primary.key === "solarent_sign") {
      await run(
        () => ContractorMasterContractApi.sendSolarentOtp(contractId),
        "کد تأیید به موبایل مسئول سولارنت ارسال شد."
      );
      return;
    }

    if (primary.key === "guarantee_upload") {
      guaranteeInputRef.current?.click();
      return;
    }

    if (primary.key === "guarantee_review") {
      if (!flags?.hasDoc) {
        toast.error("ابتدا مدرک ضمانت باید بارگذاری شود.");
        return;
      }
      const data = await run(
        () => ContractorMasterContractApi.approveGuarantee(contractId),
        "ضمانت تأیید و دسترسی عملیاتی فعال شد."
      );
      if (data !== null) await loadDetail();
    }
  };

  const pdfHref = flags?.signedPdfUrl || flags?.finalPdfUrl;
  const canDownloadPdf = Boolean(pdfHref);

  return (
    <Box sx={{ p: { xs: 1.5, md: 2 }, maxWidth: 920, mx: "auto" }} data-testid="contractor-master-operator-page">
      <BreadcrumbsComponent
        breadcrumbs={[
          { title: "مدیریت", link: "/SLN/Management" },
          { title: "قرارداد پیمانکار" },
        ]}
      />

      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }} flexWrap="wrap" useFlexGap spacing={1}>
        <Typography variant="h5" fontWeight={700}>
          قرارداد پیمانکار
        </Typography>
        <Button size="small" variant="text" onClick={() => setShowLookup((v) => !v)}>
          {lookupVisible ? "بستن جستجو" : "باز کردن قرارداد دیگر"}
        </Button>
      </Stack>

      {lookupVisible ? (
        <Card variant="outlined" sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="subtitle2" fontWeight={700} gutterBottom>
              پیدا کردن / صدور قرارداد
            </Typography>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ mb: 1.5 }}>
              <TextField
                size="small"
                fullWidth
                label="شناسه پیمانکار"
                value={contractorInfoId}
                onChange={(e) => setContractorInfoId(e.target.value)}
                helperText="اگر هنوز قرارداد ندارد"
              />
              <Button
                variant="contained"
                disabled={loading || !contractorInfoId}
                onClick={handleGenerate}
                sx={{ whiteSpace: "nowrap" }}
              >
                صدور قرارداد
              </Button>
            </Stack>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems="center">
              <TextField
                size="small"
                fullWidth
                label="شناسه قرارداد"
                value={contractId}
                onChange={(e) => setContractId(e.target.value)}
              />
              <Button
                variant="outlined"
                disabled={loading || !contractId}
                onClick={() => loadDetail()}
                sx={{ whiteSpace: "nowrap" }}
              >
                بارگذاری
              </Button>
              {loading ? <CircularProgress size={20} /> : null}
            </Stack>
          </CardContent>
        </Card>
      ) : null}

      {!view ? (
        <Card variant="outlined" sx={{ mb: 2 }}>
          <CardContent>
            <Alert severity="info" sx={{ mb: contractorInfoId ? 2 : 0 }}>
              {contractorInfoId
                ? "برای این پیمانکار هنوز قراردادی بارگذاری نشده. دکمه «صدور قرارداد» را بزنید (تایید ثبت‌نام به‌تنهایی PDF صادر نمی‌کند)."
                : "برای شروع، قرارداد را بارگذاری کنید یا برای پیمانکار جدید صادر کنید."}
            </Alert>
            {contractorInfoId ? (
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1}
                alignItems={{ xs: "stretch", sm: "center" }}
              >
                <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
                  شناسه پیمانکار: {contractorInfoId}
                </Typography>
                <Button
                  variant="contained"
                  disabled={loading}
                  onClick={handleGenerate}
                  data-testid="operator-generate-from-empty-btn"
                >
                  صدور قرارداد
                </Button>
              </Stack>
            ) : null}
          </CardContent>
        </Card>
      ) : (
        <Stack spacing={2}>
          <Card variant="outlined">
            <CardContent>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
                <Chip color="info" label={flags.statusTitle} />
                <Chip
                  variant="outlined"
                  label={`ضمانت: ${flags.guaranteeStatusTitle}`}
                />
                <Typography variant="body2" color="text.secondary">
                  {flags.contractNumber}
                  {flags.versionNumber != null ? ` · نسخه ${flags.versionNumber}` : ""}
                </Typography>
                {loading ? <CircularProgress size={18} /> : null}
              </Stack>

              <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 1 }}>
                {WORKFLOW_STEPS.map((step) => (
                  <Step key={step.id} completed={WORKFLOW_STEPS.indexOf(step) < activeStep}>
                    <StepLabel>{step.label}</StepLabel>
                  </Step>
                ))}
              </Stepper>
            </CardContent>
          </Card>

          <Card
            variant="outlined"
            sx={{
              borderColor:
                primary.severity === "success"
                  ? "success.main"
                  : primary.severity === "error"
                  ? "error.main"
                  : primary.severity === "warning"
                  ? "warning.main"
                  : "primary.main",
              borderWidth: 2,
            }}
          >
            <CardContent>
              <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ mb: 1 }}>
                {primary.key === "done" ? (
                  <CheckCircleOutlineIcon color="success" />
                ) : primary.key === "wait_contractor" || primary.key === "terminal" ? (
                  <LockOutlinedIcon color="action" />
                ) : null}
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h6" fontWeight={700}>
                    قدم بعدی: {primary.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {primary.description}
                  </Typography>
                </Box>
              </Stack>

              <input
                ref={guaranteeInputRef}
                type="file"
                accept="image/*,.pdf,application/pdf"
                hidden
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  e.target.value = "";
                  await handleUploadGuarantee(file);
                }}
              />

              {primary.showChequeForm || primary.key === "set_cheque_details" ? (
                <Stack spacing={1.5} sx={{ mt: 2 }} data-testid="operator-cheque-form">
                  <Typography variant="body2" fontWeight={700}>
                    مشخصات چک برای درج در ماده ضمانت قرارداد
                  </Typography>
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                    <TextField
                      size="small"
                      fullWidth
                      label="شناسه صیادی *"
                      value={cheque.sayadiId}
                      onChange={(e) =>
                        setCheque((p) => ({
                          ...p,
                          sayadiId: e.target.value.replace(/\D/g, "").slice(0, 16),
                        }))
                      }
                    />
                    <TextField
                      size="small"
                      fullWidth
                      label="سریال چک *"
                      value={cheque.chequeSerial}
                      onChange={(e) =>
                        setCheque((p) => ({
                          ...p,
                          chequeSerial: e.target.value.slice(0, 64),
                        }))
                      }
                    />
                  </Stack>
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                    <TextField
                      size="small"
                      fullWidth
                      label="بانک *"
                      value={cheque.bankName}
                      onChange={(e) =>
                        setCheque((p) => ({
                          ...p,
                          bankName: e.target.value.slice(0, 120),
                        }))
                      }
                    />
                    <TextField
                      size="small"
                      fullWidth
                      label="شماره حساب *"
                      value={cheque.accountNumber}
                      onChange={(e) =>
                        setCheque((p) => ({
                          ...p,
                          accountNumber: e.target.value.replace(/\D/g, "").slice(0, 20),
                        }))
                      }
                    />
                  </Stack>
                  <Typography variant="caption" color="text.secondary">
                    شعبه و نام صادرکننده توسط پیمانکار هنگام تکمیل و ارسال چک وارد می‌شود.
                  </Typography>
                  <JalaliDatePicker
                    size="small"
                    fullWidth
                    label="تاریخ مندرج در چک *"
                    value={
                      cheque.chequeDate
                        ? cheque.chequeDate instanceof Date
                          ? cheque.chequeDate
                          : new Date(cheque.chequeDate)
                        : null
                    }
                    onChange={(date) =>
                      setCheque((p) => ({ ...p, chequeDate: date }))
                    }
                    inputProps={{ "data-testid": "operator-cheque-date" }}
                  />
                  <Button
                    variant="contained"
                    disabled={loading}
                    onClick={handlePrimaryClick}
                  >
                    {primary.buttonLabel || "ثبت در قرارداد و به‌روزرسانی PDF"}
                  </Button>
                </Stack>
              ) : null}

              {primary.key === "confirm_physical_receipt" ? (
                <Stack spacing={1.5} sx={{ mt: 2 }}>
                  {flags.guaranteeSayadiId ? (
                    <Alert severity="info">
                      مشخصات ثبت‌شده: صیادی {flags.guaranteeSayadiId}
                      {flags.guaranteeChequeSerial
                        ? ` · سریال ${flags.guaranteeChequeSerial}`
                        : ""}
                      {flags.guaranteeBankName ? ` · ${flags.guaranteeBankName}` : ""}
                      {flags.guaranteeChequeDateFa
                        ? ` · تاریخ ${flags.guaranteeChequeDateFa}`
                        : ""}
                      {flags.guaranteeBranchName
                        ? ` · شعبه ${flags.guaranteeBranchName}`
                        : ""}
                      {flags.guaranteeIssuerName
                        ? ` · صادرکننده ${flags.guaranteeIssuerName}`
                        : ""}
                    </Alert>
                  ) : null}
                  {flags.hasDoc ? (
                    <Button
                      variant="outlined"
                      startIcon={<VisibilityIcon />}
                      disabled={loading}
                      onClick={handleViewGuaranteeDocument}
                      data-testid="operator-view-guarantee-btn"
                    >
                      مشاهده عکس چک بارگذاری‌شده
                    </Button>
                  ) : (
                    <Alert severity="warning">
                      هنوز عکس چکی توسط پیمانکار بارگذاری نشده است.
                    </Alert>
                  )}
                  <Alert severity="info">
                    برای پیگیری زنجیره تحویل، آورنده و تحویل‌گیرنده (کاربر فعلی) ثبت
                    می‌شوند و رکورد در «دفتر چک‌های ضمانت» ساخته می‌شود.
                  </Alert>
                  <TextField
                    label="نام آورنده چک *"
                    size="small"
                    fullWidth
                    value={receiptParties.bringerName}
                    onChange={(e) =>
                      setReceiptParties((p) => ({
                        ...p,
                        bringerName: e.target.value,
                      }))
                    }
                  />
                  <TextField
                    label="موبایل آورنده"
                    size="small"
                    fullWidth
                    value={receiptParties.bringerMobile}
                    onChange={(e) =>
                      setReceiptParties((p) => ({
                        ...p,
                        bringerMobile: e.target.value,
                      }))
                    }
                  />
                  <TextField
                    label="کد ملی آورنده"
                    size="small"
                    fullWidth
                    value={receiptParties.bringerNationalCode}
                    onChange={(e) =>
                      setReceiptParties((p) => ({
                        ...p,
                        bringerNationalCode: e.target.value,
                      }))
                    }
                  />
                  <TextField
                    label="توضیحات وصول"
                    size="small"
                    fullWidth
                    multiline
                    minRows={2}
                    value={receiptParties.officeReceiptNotes}
                    onChange={(e) =>
                      setReceiptParties((p) => ({
                        ...p,
                        officeReceiptNotes: e.target.value,
                      }))
                    }
                  />
                  <Button
                    variant="contained"
                    color="warning"
                    disabled={loading}
                    onClick={handlePrimaryClick}
                  >
                    {primary.buttonLabel || "تأیید دریافت و ثبت وصول در دفتر"}
                  </Button>
                </Stack>
              ) : null}

              {primary.key === "solarent_sign" ? (
                <Stack spacing={1.5} sx={{ mt: 2 }}>
                  <Alert severity="info">
                    ابتدا «ارسال کد تأیید» را بزنید، سپس کد پیامک‌شده را وارد کنید و حتماً
                    دکمه «تأیید امضا» را بزنید. فقط ارسال کد، قرارداد را نهایی نمی‌کند.
                  </Alert>
                  <Button
                    type="button"
                    variant="contained"
                    disabled={loading || !flags.canSendSolarentOtp}
                    onClick={handlePrimaryClick}
                  >
                    ۱) ارسال کد تأیید
                  </Button>
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                    <TextField
                      size="small"
                      fullWidth
                      label="کد تأیید دریافتی"
                      value={otpCode}
                      onChange={(e) =>
                        setOtpCode(e.target.value.replace(/\D/g, "").slice(0, 8))
                      }
                      onKeyDown={(e) => {
                        if (e.key === "Enter") e.preventDefault();
                      }}
                      inputProps={{ inputMode: "numeric", autoComplete: "one-time-code" }}
                    />
                    <Button
                      type="button"
                      variant="contained"
                      color="secondary"
                      disabled={loading || !otpCode || !flags.canVerifySolarentSign}
                      onClick={async () => {
                        const code = String(otpCode || "").trim();
                        if (!code) {
                          toast.error("کد تأیید را وارد کنید.");
                          return;
                        }
                        const data = await run(
                          () =>
                            ContractorMasterContractApi.verifySolarentSign(
                              contractId,
                              code
                            ),
                          "امضای سولارنت ثبت شد. در حال به‌روزرسانی وضعیت…"
                        );
                        setOtpCode("");
                        await loadDetail();
                        if (data == null) return;
                        const status = Number(
                          data?.status ?? data?.Status ?? 0
                        );
                        if (
                          status !== CONTRACT_STATUS.Active &&
                          status !== CONTRACT_STATUS.Signed
                        ) {
                          toast.warning(
                            "امضا ثبت شد اما وضعیت هنوز نهایی نشده؛ صفحه را تازه کنید."
                          );
                        }
                      }}
                    >
                      ۲) تأیید امضا
                    </Button>
                  </Stack>
                </Stack>
              ) : null}

              {primary.key === "guarantee_review" ? (
                <Stack spacing={1.5} sx={{ mt: 2 }}>
                  {flags.guaranteeSayadiId ? (
                    <Alert severity="info">
                      شناسه صیادی: {flags.guaranteeSayadiId}
                      {flags.guaranteeChequeSerial
                        ? ` · سریال: ${flags.guaranteeChequeSerial}`
                        : ""}
                      {flags.guaranteeBankName
                        ? ` · بانک: ${flags.guaranteeBankName}`
                        : ""}
                      {flags.guaranteeAccountNumber
                        ? ` · حساب: ${flags.guaranteeAccountNumber}`
                        : ""}
                    </Alert>
                  ) : null}
                  {(primary.allowUpload || !flags.hasDoc) && (
                    <Stack spacing={1}>
                      <Typography variant="body2" fontWeight={700}>
                        اطلاعات چک (برای آپلود/جایگزینی)
                      </Typography>
                      <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                        <TextField
                          size="small"
                          fullWidth
                          label="شناسه صیادی"
                          value={cheque.sayadiId}
                          onChange={(e) =>
                            setCheque((p) => ({
                              ...p,
                              sayadiId: e.target.value.replace(/\D/g, "").slice(0, 16),
                            }))
                          }
                        />
                        <TextField
                          size="small"
                          fullWidth
                          label="سریال"
                          value={cheque.chequeSerial}
                          onChange={(e) =>
                            setCheque((p) => ({
                              ...p,
                              chequeSerial: e.target.value.slice(0, 64),
                            }))
                          }
                        />
                      </Stack>
                      <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                        <TextField
                          size="small"
                          fullWidth
                          label="بانک"
                          value={cheque.bankName}
                          onChange={(e) =>
                            setCheque((p) => ({
                              ...p,
                              bankName: e.target.value.slice(0, 120),
                            }))
                          }
                        />
                        <TextField
                          size="small"
                          fullWidth
                          label="شماره حساب"
                          value={cheque.accountNumber}
                          onChange={(e) =>
                            setCheque((p) => ({
                              ...p,
                              accountNumber: e.target.value
                                .replace(/\D/g, "")
                                .slice(0, 20),
                            }))
                          }
                        />
                      </Stack>
                    </Stack>
                  )}
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    <Button
                      variant="outlined"
                      disabled={loading || !flags.hasDoc}
                      onClick={async () => {
                        setLoading(true);
                        try {
                          const blob =
                            await ContractorMasterContractApi.downloadGuaranteeDocument(
                              contractId
                            );
                          if (!blob) {
                            toast.error("دانلود ناموفق بود.");
                            return;
                          }
                          const url = URL.createObjectURL(blob);
                          window.open(url, "_blank", "noopener,noreferrer");
                          setTimeout(() => URL.revokeObjectURL(url), 30_000);
                        } finally {
                          setLoading(false);
                        }
                      }}
                    >
                      مشاهده عکس چک
                    </Button>
                    {primary.allowUpload ? (
                      <Button
                        variant="outlined"
                        disabled={loading}
                        onClick={() => guaranteeInputRef.current?.click()}
                      >
                        جایگزینی مدرک
                      </Button>
                    ) : null}
                  </Stack>
                  <Button
                    variant="contained"
                    color="success"
                    disabled={loading || !flags.hasDoc}
                    onClick={handlePrimaryClick}
                  >
                    تأیید ضمانت و فعال‌سازی
                  </Button>
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                    <TextField
                      size="small"
                      fullWidth
                      label="دلیل رد (الزامی برای رد)"
                      value={rejectReason}
                      onChange={(e) => setRejectReason(e.target.value)}
                    />
                    <Button
                      variant="outlined"
                      color="error"
                      disabled={loading || !flags.hasDoc || !rejectReason.trim()}
                      onClick={async () => {
                        const data = await run(
                          () =>
                            ContractorMasterContractApi.rejectGuarantee(
                              contractId,
                              rejectReason
                            ),
                          "ضمانت رد شد."
                        );
                        if (data !== null) {
                          setRejectReason("");
                          await loadDetail();
                        }
                      }}
                    >
                      رد ضمانت
                    </Button>
                  </Stack>
                </Stack>
              ) : null}

              {primary.key === "guarantee_upload" ? (
                <Stack spacing={1.25} sx={{ mt: 2 }}>
                  <Typography variant="body2" fontWeight={700}>
                    اطلاعات چک صیادی
                  </Typography>
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                    <TextField
                      size="small"
                      fullWidth
                      label="شناسه صیادی"
                      value={cheque.sayadiId}
                      onChange={(e) =>
                        setCheque((p) => ({
                          ...p,
                          sayadiId: e.target.value.replace(/\D/g, "").slice(0, 16),
                        }))
                      }
                    />
                    <TextField
                      size="small"
                      fullWidth
                      label="سریال چک"
                      value={cheque.chequeSerial}
                      onChange={(e) =>
                        setCheque((p) => ({
                          ...p,
                          chequeSerial: e.target.value.slice(0, 64),
                        }))
                      }
                    />
                  </Stack>
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                    <TextField
                      size="small"
                      fullWidth
                      label="بانک"
                      value={cheque.bankName}
                      onChange={(e) =>
                        setCheque((p) => ({
                          ...p,
                          bankName: e.target.value.slice(0, 120),
                        }))
                      }
                    />
                    <TextField
                      size="small"
                      fullWidth
                      label="شماره حساب"
                      value={cheque.accountNumber}
                      onChange={(e) =>
                        setCheque((p) => ({
                          ...p,
                          accountNumber: e.target.value
                            .replace(/\D/g, "")
                            .slice(0, 20),
                        }))
                      }
                    />
                  </Stack>
                </Stack>
              ) : null}

              {primary.buttonLabel &&
              primary.key !== "solarent_sign" &&
              primary.key !== "guarantee_review" &&
              primary.key !== "set_cheque_details" &&
              primary.key !== "confirm_physical_receipt" ? (
                <Button
                  variant="contained"
                  size="large"
                  sx={{ mt: 2 }}
                  disabled={loading}
                  onClick={handlePrimaryClick}
                >
                  {primary.buttonLabel}
                </Button>
              ) : null}

              {primary.key === "wait_contractor" || primary.key === "done" ? (
                <Button
                  variant="outlined"
                  sx={{ mt: 2 }}
                  disabled={loading}
                  onClick={() => loadDetail()}
                >
                  تازه‌سازی وضعیت
                </Button>
              ) : null}
            </CardContent>
          </Card>

          <Accordion disableGutters elevation={0} sx={{ border: "1px solid", borderColor: "divider" }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle2">جزئیات و ابزارهای فرعی</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={1.5}>
                {flags.verificationCode ? (
                  <Typography variant="body2" sx={{ direction: "ltr", unicodeBidi: "isolate" }}>
                    کد تأیید قرارداد: {flags.verificationCode}
                  </Typography>
                ) : null}
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {canDownloadPdf ? (
                    <Button
                      variant="outlined"
                      size="small"
                      disabled={loading}
                      onClick={handleDownloadPdf}
                      data-testid="operator-download-pdf-btn"
                    >
                      دانلود PDF
                    </Button>
                  ) : null}
                  <Button
                    variant="contained"
                    color="warning"
                    size="small"
                    disabled={loading || !contractId}
                    onClick={handleRegenerateContent}
                    data-testid="operator-regenerate-content-btn"
                  >
                    بازتولید محتوا (اطلاعات پلتفرم)
                  </Button>
                  <Button
                    variant="outlined"
                    color="secondary"
                    size="small"
                    disabled={loading || !contractId}
                    onClick={handleCreateRevision}
                    data-testid="operator-create-revision-btn"
                  >
                    ایجاد نسخه جدید
                  </Button>
                  {flags.signedPdfUrl ? (
                    <Button
                      variant="outlined"
                      size="small"
                      disabled={loading}
                      onClick={handleRegenerateSignedPdf}
                      data-testid="operator-regenerate-signed-pdf-btn"
                    >
                      بازتولید PDF امضاشده
                    </Button>
                  ) : null}
                  <Button
                    variant="outlined"
                    size="small"
                    disabled={loading}
                    onClick={async () => {
                      const res =
                        await ContractorMasterContractApi.getSignatureEvidence(contractId);
                      if (!res.isSuccess) toast.error(res.message);
                      else toast.info("شواهد امضا دریافت شد.");
                    }}
                  >
                    شواهد امضای پیمانکار
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    disabled={loading}
                    onClick={() => loadDetail()}
                  >
                    تازه‌سازی
                  </Button>
                </Stack>
                <ContractTimeline events={timeline} title="تاریخچه" />
              </Stack>
            </AccordionDetails>
          </Accordion>
        </Stack>
      )}
    </Box>
  );
};

export default ContractorMasterContractOperatorPage;
